import { Button, Divider, Icon, Intent, Spinner, SpinnerSize } from "@blueprintjs/core";
import "cleave.js/dist/addons/cleave-phone.be";
import { IconNames } from "@blueprintjs/icons";
import {
  FGCheckboxInput,
  FGCustomInput,
  FGListen,
  FGMaskInput,
  FGSelectInput,
  FGTextAreaInput,
  FGTextInput,
  FieldGroup,
  FieldSet,
  showError,
  showSuccess,
  useApiEffect,
  useFGFormik,
} from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import styled from "styled-components";
import * as Yup from "yup";

import { CountryApi, EPlatform, MembershipHIstoryApi, PersonApi, PersonDto } from "../../../../api";
import {
  FGCountrySelect,
  FGPhoneInput,
  FGSRSHCheckboxInput,
  FGSRSHDateMaskInput,
  SharepointButton,
  SmallFormGenerator,
} from "../../../../components";
import { useAuth, useDialog, useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useCrudApi, useTl } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";
import { vatNumberValidate } from "../../../../utils";
import { PersonDogsPage } from "./PersonDogsPage";

const SpanMember = styled.span`
  font-size: 10pt;
  vertical-align: text-bottom;
  margin: 0px 32px 0px 4px;
`;

export interface IPersonDetailsPageProps {
  personId: number;
  editMode: boolean;
}

export const PersonDetailsPage: React.FunctionComponent<IPersonDetailsPageProps> = ({ personId, editMode }) => {
  const api = useApiService(PersonApi);
  const membershipHistoryApi = useApiService(MembershipHIstoryApi);
  const countryApi = useApiService(CountryApi);
  const { t } = useTl();
  const { hasAction } = useAuth();
  const { currentLanguage } = useGlobalData();
  const { showDialogPromise } = useDialog();
  const history = useHistory();

  const [postalCode, setPostalCode] = React.useState("");
  const [isBelgium, setIsBelgium] = React.useState<boolean>(false);
  const [saveBcLoading, setsaveBcLoading] = React.useState(false);

  const [baseCountryId, baseCountryLoading] = useApiEffect(async () => await countryApi.getBelgianId(), []);
  const [languages, lloading] = useReferential((a) => a.getLanguages(), true, []);
  const [postalCodes, pcloading] = useReferential((a) => a.getStringifiedBelgianPostalCodes(), true, []);
  const [localities, locloading] = useReferential(
    (a) => a.getStringifiedBelgianLocalityFromPostalCode(postalCode ?? ""),
    false,
    [postalCode]
  );
  const [isMember, isMemberLoading] = useApiEffect(async () => {
    const today = new Date();
    return (await membershipHistoryApi.testMembershipHistoryExistance(today, today, personId))?.value;
  }, [membershipHistoryApi, personId]);

  const [personSalutations, ploading] = useReferential(
    (a) => a.getPersonSalutations(currentLanguage.languageId),
    true,
    [currentLanguage.languageId]
  );

  const { data, loading, saveItem, saving, deleteItem, deleting, refresh } = useCrudApi<PersonDto>(
    React.useMemo(
      () => ({
        getApiFn: (id = personId) => (id > 0 ? api.get(id) : new PersonDto({ countryId: baseCountryId }).toJSON()),
        saveApiFn: (d: PersonDto) => api.save(EPlatform.CARE4DOGS, d),
        onSavedRoute: (d) => `${ERoutes.people}/${d.personId}/detail/edit`,
        deleteApiFn: (d) => api.delete(d.personId),
        onDeletedRoute: () => ERoutes.people,
      }),
      [api, baseCountryId, personId]
    ),
    [personId]
  );

  React.useEffect(() => {
    if (data && postalCode === "") {
      setPostalCode(data.postalCode);
    }
  }, [data, postalCode]);

  const saveBc = React.useCallback(async () => {
    setsaveBcLoading(true);
    const dialogResult = await showDialogPromise({
      message: t(ETLCodes.BCSaveConfirmationMessage),
      title: t(ETLCodes.Warning),
    });
    if (dialogResult !== "yes") {
      setsaveBcLoading(false);
      return;
    }
    try {
      await api.saveBc(data?.personId);
      showSuccess(t(ETLCodes.SaveSuccess));
      refresh();
    } catch {
      showError(t(ETLCodes.Error));
    }

    setsaveBcLoading(false);
  }, [api, data, refresh, showDialogPromise, t]);

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      firstName: Yup.string().nullable().required(t(ETLCodes.Required)),
      lastName: Yup.string().nullable().required(t(ETLCodes.Required)),
      languageId: Yup.number().nullable().required(t(ETLCodes.Required)),
      personHonorificId: Yup.string().nullable().required(t(ETLCodes.Required)), //number().required(),
      street: Yup.string().nullable().required(t(ETLCodes.Required)),
      number: Yup.string().nullable().required(t(ETLCodes.Required)),
      postalCode: Yup.string().nullable().required(t(ETLCodes.Required)),
      countryId: Yup.number()
        .nullable()
        .required(t(ETLCodes.Required))
        .test("is-vat-be", t(ETLCodes.VATNotCorrespondingToCountry), async function (value) {
          if (!this.parent?.vatNumber) return true;
          const belgium = await countryApi.isBelgian(value);
          return !belgium.value || (belgium.value && this.parent?.vatNumber.substr(0, 2) === "BE");
        }),
      city: Yup.string().nullable().required(t(ETLCodes.Required)),
      email: Yup.string()
        .nullable()
        .email()
        .test("required", t(ETLCodes.Required), async function (value) {
          if (
            (this.parent.phoneNumber === undefined || this.parent.phoneNumber === null) &&
            (this.parent.mobilePhoneNumber === undefined || this.parent.mobilePhoneNumber === null)
          ) {
            return value !== null && value !== undefined;
          }
          return true;
        })
        .test("email-unique", t(ETLCodes.EmailIsAlreadyInUse), async function (value) {
          if (!value) return true;
          const res = await api.isEmailUnique(value, this.parent.personId);
          if (res) {
            return !res.value;
          }
        }),
      phoneNumber: Yup.string()
        .nullable()
        .test("required", t(ETLCodes.Required), async function (value) {
          if (
            (this.parent.email === undefined || this.parent.email === null) &&
            (this.parent.mobilePhoneNumber === undefined || this.parent.mobilePhoneNumber === null)
          ) {
            return value !== null && value !== undefined;
          }
          return true;
        }),
      mobilePhoneNumber: Yup.string()
        .nullable()
        .test("required", t(ETLCodes.Required), async function (value) {
          if (
            (this.parent.phoneNumber === undefined || this.parent.phoneNumber === null) &&
            (this.parent.email === undefined || this.parent.email === null)
          ) {
            return value !== null && value !== undefined;
          }
          return true;
        }),
      vatNumber: Yup.string()
        .nullable()
        .test("validate", t(ETLCodes.InvalidVAT), async function (value) {
          console.log("VATValue", value);
          if (!this.parent.countryId || !value || value === "BE") return true;
          const belgium = await countryApi.isBelgian(this.parent.countryId);
          if (belgium.value) return vatNumberValidate(value);
          return true;
        }),
    });
  }, [api, countryApi, t]);

  const formik = useFGFormik({
    initialValues: data,
    onSubmit: async (d) => await saveItem(d),
    validationSchema: FormSchema,
  });

  React.useEffect(() => {
    if (!!baseCountryId && !personId && !formik?.values?.countryId) {
      formik?.setFieldValue("countryId", baseCountryId);
    }
  }, [baseCountryId, personId, formik]);

  function IsAMember(props) {
    if (isMemberLoading) {
      return <Spinner size={SpinnerSize.SMALL} />;
    } else if (isMember) {
      return (
        <>
          <Icon icon="endorsed" intent="success" />
          <SpanMember>{t(ETLCodes.IsAMember)}</SpanMember>
        </>
      );
    } else {
      return (
        <>
          <Icon icon="disable" intent="warning" />
          <SpanMember>{t(ETLCodes.IsNotAMember)}</SpanMember>
        </>
      );
    }
  }
  const [shpLoading, setShpLoading] = React.useState(false);
  const existsInShp = React.useMemo(() => !!data?.sharepointUri, [data?.sharepointUri]);
  const openOrCreateInShp = React.useCallback(async () => {
    setShpLoading(true);
    if (!!data?.sharepointUri) {
      window.open(data?.sharepointUri, "_blank");
      setShpLoading(false);
    } else {
      try {
        await api.createInSharepoint(personId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setShpLoading(false);
      }
    }
  }, [api, data?.sharepointUri, personId, refresh]);

  const prefix = React.useMemo(() => (data?.vatNumber?.startsWith("BE") ? "" : "BE"), [data?.vatNumber]);

  return (
    !baseCountryLoading && (
      <>
        <SmallFormGenerator
          formik={formik}
          initialValues={data}
          onSubmit={saveItem}
          editMode={editMode}
          loading={loading}
          onCancel={() => history.push(ERoutes.people)}
          saving={saving}
          deleting={deleting}
          showDeleteButton={personId > 0 || data?.bcIdentifier !== null}
          onDelete={deleteItem}
          validationSchema={FormSchema}
        >
          <FGListen
            field="countryId"
            onChanged={(value, formik) => {
              if (value !== data?.countryId) {
                formik.setFieldValue("vatNumber", "");
              }

              if (!value && personId === 0) {
                formik.setFieldValue("countryId", baseCountryId);
                setIsBelgium(true);
              } else {
                if (value === baseCountryId) {
                  setIsBelgium(true);
                } else {
                  setIsBelgium(false);
                }
              }
            }}
          />
          <FGListen
            field="postalCode"
            onChanged={(value) => {
              if (isBelgium && value) {
                setPostalCode(value);
              }
            }}
          />
          <FieldSet
            rightElement={
              personId > 0 && (
                <>
                  <IsAMember />
                  <SharepointButton existsInShp={existsInShp} loading={shpLoading} onClick={openOrCreateInShp} />

                  {data && !data.bcIdentifier ? (
                    <Button
                      icon={IconNames.CREDIT_CARD}
                      onClick={saveBc}
                      text={t(ETLCodes.SaveNav)}
                      intent={Intent.PRIMARY}
                      loading={saveBcLoading}
                    />
                  ) : null}
                </>
              )
            }
            title={t(ETLCodes.Contact)}
          >
            {data?.bcIdentifier ? (
              <FGTextInput label={t(ETLCodes.BCIdentifier)} name="bcIdentifier" readonly />
            ) : (
              <FGCheckboxInput label={t(ETLCodes.BCIdentifier)} name="BcIdentifier" readonly />
            )}
            <FieldGroup columns={2}>
              <FGSelectInput
                name="personHonorificId"
                items={personSalutations}
                loading={ploading}
                label={t(ETLCodes.Salutation)}
              />
            </FieldGroup>
            <FieldGroup columns={2}>
              <FGTextInput name="lastName" label={t(ETLCodes.LastName)} />
              <FGTextInput name="firstName" label={t(ETLCodes.FirstName)} />
            </FieldGroup>
            <FieldGroup columns={2}>
              {personId > 0 && (
                <FGTextInput name="computedName" label={t(ETLCodes.CompleteName)} visible={!loading} readonly />
              )}
            </FieldGroup>

            <FieldGroup columns={2}>
              <FGTextInput name="email" label={t(ETLCodes.Email)} requiredMark />
            </FieldGroup>
            <FieldGroup columns={2}>
              <FGPhoneInput name="phoneNumber" label={t(ETLCodes.PhoneNumber)} requiredMark />
              <FGPhoneInput name="mobilePhoneNumber" label={t(ETLCodes.MobilePhoneNumber)} requiredMark />
            </FieldGroup>
            <FieldGroup columns={2}>
              <FGSelectInput
                name="languageId"
                items={languages}
                loading={lloading}
                label={t(ETLCodes.Language)}
                requiredMark
              />
              <FGCustomInput label={t(ETLCodes.Kennel)} visible={!!data?.kennelId}>
                {(ctx) => (
                  <Link target="_blank" to={`${ERoutes.kennel}/${data?.kennelId}/detail/view`}>
                    {data?.kennelName}
                  </Link>
                )}
              </FGCustomInput>
            </FieldGroup>
          </FieldSet>
          <Divider />
          <FieldGroup columns={2}>
            <FGSRSHDateMaskInput name="birthDate" label={t(ETLCodes.BirthDate)} />
            <FGSRSHDateMaskInput name="deathDate" label={t(ETLCodes.DeathDate)} />
          </FieldGroup>
          <FieldGroup columns={2}>
            <FGSRSHCheckboxInput name="isBadPayer" label={t(ETLCodes.IsBadPayer)} />
          </FieldGroup>
          <FieldGroup columns={2}>
            <FGSRSHDateMaskInput name="bannedFrom" label={t(ETLCodes.BannedFrom)} />
            <FGSRSHDateMaskInput name="bannedTo" label={t(ETLCodes.BannedTo)} />
          </FieldGroup>
          <Divider />
          <FieldGroup>
            <FieldGroup columns={2}>
              <FGTextInput name="street" label={t(ETLCodes.Street)} />
            </FieldGroup>
            <FieldGroup columns={2}>
              <FGTextInput name="number" label={t(ETLCodes.StreetNumber)} />
            </FieldGroup>
            <FieldGroup columns={2}>
              {isBelgium ? (
                <FGSelectInput
                  name="postalCode"
                  items={postalCodes}
                  loading={pcloading}
                  label={t(ETLCodes.PostalCode)}
                />
              ) : (
                <FGTextInput name="postalCode" label={t(ETLCodes.PostalCode)} />
              )}
            </FieldGroup>
            <FieldGroup columns={2}>
              {isBelgium ? (
                <FGSelectInput name="city" items={localities} loading={locloading} label={t(ETLCodes.City)} />
              ) : (
                <FGTextInput name="city" label={t(ETLCodes.City)} />
              )}
            </FieldGroup>
            <FieldGroup columns={2}>
              <FGCountrySelect name="countryId" label={t(ETLCodes.Country)} isCleareable={false} />
            </FieldGroup>
            <FieldGroup columns={2}>
              <FGTextAreaInput name="remark" label={t(ETLCodes.Remark)} />
            </FieldGroup>
          </FieldGroup>
          <Divider />
          <FieldGroup columns={2}>
            {isBelgium ? (
              <FGMaskInput
                cleaveOptions={{
                  numeral: false,
                  delimiter: ".",
                  blocks: [2, 4, 3, 3],
                  prefix: prefix,
                }}
                name="vatNumber"
                label={t(ETLCodes.VATNumber)}
              />
            ) : (
              <FGTextInput
                name="vatNumber"
                label={t(ETLCodes.VATNumber)}
                maxLength={20}
                readonly={!!data?.bcIdentifier && !!data?.vatNumber}
              />
            )}
            <FGTextInput name="societyName" label={t(ETLCodes.SocietyName)} maxLength={50} />
            <FGCheckboxInput
              name="isKnownPerson"
              label={t(ETLCodes.IsKnownPerson)}
              readonly={!hasAction("HANDLEKNOWNPERSON")}
            />
          </FieldGroup>
        </SmallFormGenerator>
        <PersonDogsPage personId={personId} />
      </>
    )
  );
};
